import PreviewLayout from "./preview-layout";
import PreviewField from "./preview-field";
import type { FlowSection, FlowSectionField } from "../form-builder/types";
import type { BrandKit } from "@/modules/brand";

interface PreviewNegativeFeedbackProps {
	id: string;
	section: FlowSection;
	brandName: string;
	brandLogo: string | null;
	brandkit: BrandKit;
	isSelected: boolean;
	onTitleChange: (title: string) => void;
	onDescriptionChange: (description: string) => void;
	onFieldUpdate?: (fieldId: string, updates: Partial<FlowSectionField>) => void;
	isDarkMode?: boolean;
}

export default function PreviewNegativeFeedback({ id, section, brandName, brandLogo, brandkit, isSelected, onTitleChange, onDescriptionChange, onFieldUpdate, isDarkMode = false }: PreviewNegativeFeedbackProps) {
	void isSelected;

	const visibleFields = (section.fields ?? []).filter((f) => f.is_visible).sort((a, b) => a.sort_order - b.sort_order);

	return (
		<PreviewLayout
			id={id}
			brandName={brandName}
			brandLogo={brandLogo}
			brandkit={brandkit}
			title={section.title}
			titlePlaceholder="Sorry to hear that"
			subtitle={section.description}
			subtitlePlaceholder="Add a description..."
			continueLabel="Submit"
			editableTitle
			editableSubtitle
			onLocalTitleChange={onTitleChange}
			onLocalSubtitleChange={onDescriptionChange}
			isDarkMode={isDarkMode}
		>
			<div className="space-y-8">
				{visibleFields.length > 0 ? (
					visibleFields.map((field) => (
						<PreviewField
							key={field.id}
							field={field}
							onLabelChange={(label) => onFieldUpdate?.(field.id, { label })}
							onPlaceholderChange={(placeholder) => onFieldUpdate?.(field.id, { placeholder })}
							isDarkMode={isDarkMode}
						/>
					))
				) : (
					<>
						{/* Fallback: hardcoded fields for backwards compatibility */}
						<div>
							<label className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`} style={{ fontFamily: "var(--font-body, inherit)" }}>
								Name <span className="text-red-500">*</span>
							</label>
							<input
								type="text"
								placeholder="Your name"
								disabled
								className={`w-full border rounded-lg shadow-sm py-3 px-5 text-sm cursor-not-allowed ${isDarkMode ? "bg-gray-900 border-gray-800 text-gray-500 placeholder-gray-600" : "bg-white border-gray-200 text-gray-400 placeholder-gray-400"}`}
								style={{ fontFamily: "var(--font-body, inherit)" }}
							/>
						</div>
						<div>
							<label className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`} style={{ fontFamily: "var(--font-body, inherit)" }}>
								Feedback
							</label>
							<div
								className={`w-full border rounded-lg shadow-sm py-3 px-5 text-sm cursor-not-allowed ${isDarkMode ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200"}`}
								style={{ minHeight: "140px", fontFamily: "var(--font-body, inherit)" }}
							>
								<span className={`font-light ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>What could we do better?</span>
							</div>
						</div>
						<div>
							<label className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`} style={{ fontFamily: "var(--font-body, inherit)" }}>
								Email
							</label>
							<input
								type="email"
								placeholder="jane@company.com"
								disabled
								className={`w-full border rounded-lg shadow-sm py-3 px-5 text-sm cursor-not-allowed ${isDarkMode ? "bg-gray-900 border-gray-800 text-gray-500 placeholder-gray-600" : "bg-white border-gray-200 text-gray-400 placeholder-gray-400"}`}
								style={{ fontFamily: "var(--font-body, inherit)" }}
							/>
						</div>
						<div>
							<label className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`} style={{ fontFamily: "var(--font-body, inherit)" }}>
								Phone
							</label>
							<input
								type="tel"
								placeholder="+1 (555) 123-4567"
								disabled
								className={`w-full border rounded-lg shadow-sm py-3 px-5 text-sm cursor-not-allowed ${isDarkMode ? "bg-gray-900 border-gray-800 text-gray-500 placeholder-gray-600" : "bg-white border-gray-200 text-gray-400 placeholder-gray-400"}`}
								style={{ fontFamily: "var(--font-body, inherit)" }}
							/>
						</div>
					</>
				)}
			</div>
		</PreviewLayout>
	);
}
